<template>
  <!-- Empty state -->
  <EmptyMessage
    v-if="!loading && !hasIssues"
    :message="$t('empty_states.sets.no_issues')"
    :modifiers="['grow', 'center']"
    :title="$t('shafts.3dModel.no_set_selection.title')"
  />
  <!-- Loading state -->
  <template v-if="loading">
    <SkeletonElement
      :height="80"
      :style="'2px'"
    />
    <SkeletonElement
      v-for="index in 10"
      :key="index"
      :height="16"
      :style="'marginBottom: 2px'"
    />
  </template>
  <!-- Content -->
  <div
    v-if="!loading && hasIssues"
    class="shaft-overview"
  >
    <!-- Headgear -->
    <div class="headgear">
      <div class="headgear__column" />
      <div class="headgear__column" />
      <div class="headgear__column headgear__column--center">
        <div class="headgear__cap" />
        <div class="headgear__left-support" />
        <div class="headgear__right-support" />
        <div class="headgear__center-support" />
        <div class="headgear__drum" />
      </div>
    </div>
    <!-- main container -->
    <div class="main">
      <div class="set-grid">
        <div
          v-for="set in numberOfSets"
          :id="`set_${set}`"
          :key="set"
          class="set-grid__row"
        >
          <div
            class="
              set-grid__row-column set-grid__row-column--nums
              typo-super-tiny
            "
          >
            <strong
              v-if="set % 5 === 0"
              aria-label="set highlighted"
              class="set-grid__row-column--marker"
            >
              {{ set }}
            </strong>
          </div>
          <div class="set-grid__row-column set-grid__row-column--border">
            <template
              v-for="stationItem in stations"
              :key="stationItem"
            >
              <div
                v-if="setIsStation(set, stationItem)"
                :class="{ 'station--color': setIsStation(set, stationItem) }"
              />
            </template>
          </div>
          <div
            class="
              set-grid__row-column
              set-grid__row-column--border
              set-grid__row-column--center
            "
          >
            <template
              v-for="stationItem in stations"
              :key="stationItem"
            >
              <div
                v-if="setIsStation(set, stationItem)"
                class="station station--station-dashed"
              />
            </template>
          </div>
          <div class="set-grid__row-column set-grid__row-column--border">
            <template
              v-for="stationItem in stations"
              :key="stationItem"
            >
              <div
                v-if="setIsStation(set, stationItem)"
                :class="{ 'station--color': setIsStation(set, stationItem) }"
              >
                <router-link
                  v-if="setIsStationStart(set, stationItem)"
                  class="station__name typo-tiny"
                  :to="{
                    name: 'shaftV2IssuesTypeStation',
                    params: {
                      stationId: stationItem.id,
                      issueType: 'stations',
                    },
                  }"
                >
                  {{ stationItem.name }}
                  <div
                    class="set-indicator"
                    :style="`height: ${calculateStationHeight(stationItem)}px`"
                  />
                  <span class="station__name-tooltip">
                    {{ $t('shafts.overview.set') }} {{ stationItem.start_set }}-{{ stationItem.end_set }}
                  </span>
                </router-link>
              </div>
            </template>
          </div>
        </div>
        <div class="shaft-overview__base" />
      </div>
      <div
        v-for="setGroup in groupedSetData"
        :key="setGroup.id"
        class="set-group"
      >
        <div class="set-group__row">
          <div class="set-group__row-column" />
          <div class="set-group__row-column" />
          <div class="set-group__row-column set-group__row-column--center">
            <div class="set-group__row-item">
              <ShaftOverviewIndicator
                v-if="setGroup.issue_count !== 0"
                :data="setGroup"
              />
            </div>
          </div>
          <div class="set-group__row-column" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

import EmptyMessage from '@/components/generic/empty_message.vue';
import ShaftOverviewIndicator from '@/components/shaftsV2/overview/shaft_overview_indicator.vue';
import SkeletonElement from '@/components/generic/skeleton_element.vue';

const props = defineProps({
  /** Data representing a group of sets */
  groupedSetData: { type: Array, required: false, default: () => [] },
  /** The number of sets a shaft has */
  numberOfSets: { type: Number, required: false, default: 0 },
  /** Stations data */
  stations: { type: Array, required: false, default: () => [] },
  /** If you add the `?group_size=10` param when calling the data, be sure to update this prop with the same value eg. 10 */
  groupedSetSize: { type: Number, required: false, default: 5 },
  /** The current loading state */
  loading: { type: Boolean, required: false, default: false }
});

function setIsStationStart(set, stationItem) {
  return set === stationItem.start_set;
}

function setIsStation(set, stationItem) {
  return set >= stationItem.start_set && set <= stationItem.end_set;
}

function calculateStationHeight(station) {
  return 16 * (station.end_set - station.start_set) + 15;
}

const configureGroupSize = computed(() => {
  return props.groupedSetSize;
});

const hasIssues = computed(() => {
  let setsWithIssues = props.groupedSetData.filter((setData) => setData.issue_count !== 0);
  return setsWithIssues.length !== 0;
});
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$border-width: 1px;
$border-color: map-get($foreground-color, default);
$border-style-dashed: $border-width dashed $border-color;
$border-style: $border-width solid $border-color;

$main-shaft-column-size: rem(40);
$set-number-col-size: rem(35);

$set-height: rem(16);
$sets-per-group: v-bind(configureGroupSize);
$group-height: calc($set-height * $sets-per-group);

$headgear-height: rem(100);

@mixin grid-column-structure {
  grid-template-columns: $set-number-col-size 1fr $main-shaft-column-size 1fr;
}
.shaft-overview {
  padding-top: bu(1);

  &__base {
    border-top: $border-style;
    height: bu(5);
  }
}

// HEADGEAR
.headgear {
  @include grid-column-structure;
  border-bottom: $border-style;
  display: grid;
  height: $headgear-height;

  &__column {
    &--center {
      border-left: $border-style;
      border-right: $border-style;
      border-top: $border-style;
      position: relative;
    }
  }

  &__cap {
    border-bottom: $border-style;
    border-top: $border-style;
    height: rem(4);
    left: rem(-2);
    position: absolute;
    top: rem(-4);
    width: calc($main-shaft-column-size + rem(2));
  }

  @mixin sideSupport {
    background-color: $border-color;
    bottom: bu(-0.375);
    height: 80%;
    position: absolute;
    width: $border-width;
  }

  &__left-support {
    @include sideSupport;
    left: bu(-1.875);
    transform: rotate(21deg);
  }

  &__right-support {
    @include sideSupport;
    right: bu(-1.875);
    transform: rotate(-21deg);
  }

  &__center-support {
    border-top: $border-style;
    position: absolute;
    top: bu(3.25);
    width: 100%;
  }

  &__drum {
    border-radius: 100%;
    border: $border-style;
    height: bu(3.75);
    margin: bu(1.5) auto;
    width: bu(3.75);
  }
}

// MAIN CONTAINER
.main {
  position: relative;
}

// SET GRID & ROWS
.set-grid {
  @include positionAbsoluteZero;

  &__row {
    @include grid-column-structure;
    display: grid;
    height: $set-height;
  }

  &__row-column {
    position: relative;

    &--center {
      border-left: $border-style;
      border-right: $border-style;
      position: relative;
      text-align: center;
    }

    &--border {
      border-bottom: 0.5px solid transparentize(map-get($background-color, lightest), 0.5);
    }

    &--nums {
      align-items: center;
      border-right: $border-width solid map-get($background-color, default);
      color: transparentize(map-get($foreground-color, dark), 0.85);
      display: flex;
      font-weight: 300;
      justify-content: flex-end;
      padding-right: bu(2);

      strong {
        color: map-get($foreground-color, dark);
      }
    }

    &--marker {
      &:after {
        border-bottom: 0.5px solid transparentize(map-get($background-color, lightest), 0.4);
        bottom: 0;
        content: '';
        height: 1px;
        position: absolute;
        right: 0;
        width: bu(1.5);
      }
    }
  }
}

// GROUPED SETS
.set-group {
  z-index: 1;

  &__row {
    @include grid-column-structure;
    display: grid;
    height: $group-height;
  }

  &__row-column {
    &--center {
      display: flex;
      flex-direction: column;
    }
  }

  &__row-item {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: center;
    position: relative;
  }
}

// STATIONS
.station {
  background: map-get($background-color, darkest);
  bottom: 0;
  height: 100%;
  left: -$border-width;
  position: absolute;
  right: 0;
  top: 0;
  width: $main-shaft-column-size;

  &--station-dashed {
    border-left: $border-style-dashed;
    border-right: $border-style-dashed;
  }

  &__name {
    $block: &;
    cursor: pointer;
    font-style: normal;
    font-weight: 300;
    left: bu(4);
    letter-spacing: rem(-0.23);
    line-height: bu(1.75);
    position: absolute;
    color: map-get($foreground-color, dark);

    &:hover {
      outline: none;
      color: map-get($important-color, default);
      letter-spacing: rem(-0.23);

      #{$block}-tooltip {
        display: inline-flex;
        transition: $transition-duration ease-in-out;
        margin-left: bu(2.5);
        color: $white;
      }

      .set-indicator {
        @include setOpacityAndVisibility;
        transition: $transition-duration ease-in-out;
      }
    }

    &-tooltip {
      display: none;
      background-color: darken(map-get($background-color, darkest), 5%);
      border-radius: map-get($border-radius, radius-8);
      margin-left: -bu(1.25);
      padding: bu(1);
      position: absolute;
      top: 50%;
      transform: translate(0, -50%);
      transition: $transition-duration ease-in-out;
      width: max-content;
      z-index: 10;

      &:before {
        border-bottom: bu(1) solid transparent;
        border-right: bu(1) solid darken(map-get($background-color, darkest), 5%);
        border-top: bu(1) solid transparent;
        content: '';
        left: bu(-1);
        position: absolute;
        top: 50%;
        transform: translate(0, -50%);
        z-index: 99;
      }
    }
  }

  &--color {
    @include positionAbsoluteZero;
    background: map-get($background-color, darkest);
  }
}

$indicatorWidth: rem(4);

@mixin indicatorPseudoPosition {
  content: '';
  position: absolute;
  left: -$indicatorWidth;
  width: $indicatorWidth;
  height: 1px;
  background-color: map-get($important-color, default);
}
.set-indicator {
  @include setOpacityAndVisibility(false);
  border-bottom: 1px solid map-get($important-color, default);
  border-left: 1px solid map-get($important-color, default);
  border-top: 1px solid map-get($important-color, default);
  left: bu(-1);
  position: absolute;
  right: 0;
  top: 0;
  transition: $transition-duration ease-in-out;
  width: $indicatorWidth;
  z-index: 99;

  &:before {
    @include indicatorPseudoPosition;
    top: -1px;
  }

  &:after {
    @include indicatorPseudoPosition;
    bottom: -1px;
  }
}
</style>
